import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Bot, Eye, Grid3X3, KeyRound, UserPlus } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { timeAgo } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Field, PageHeader, UserAvatar } from "@/components/common/Bits";
import { Tag } from "@/components/common/StatusChip";
import BotKeysDialog from "@/components/users/BotKeysDialog";

function UserDialog({ open, onOpenChange, canMatrix, onBotCreated }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "", name: "", kind: "human" });
  const [busy, setBusy] = useState(false);
  const isBot = form.kind === "bot";
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/users", isBot ? { name: form.name, kind: "bot" } : form);
      toast.success(isBot ? `Bot ${form.name} added. Give it capabilities in the matrix, then issue an API key.` : `${form.name} added. They can sign in with a code sent to ${form.email}.`);
      qc.invalidateQueries({ queryKey: ["users"] });
      onOpenChange(false);
      setForm({ email: "", name: "", kind: "human" });
      if (isBot) onBotCreated?.(data);
      else if (canMatrix) navigate("/permissions");
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="user-dialog">
        <form onSubmit={submit} className="space-y-4">
          <DialogHeader><DialogTitle>Add a {isBot ? "bot" : "user"}</DialogTitle>
            <DialogDescription>{isBot ? "Bots never use the browser: they call the API with their own key and get exactly the capabilities you tick for them in the permission matrix. Their actions show under the bot's name."
              : "They sign in with a one-time code emailed to this address. New users start with no access — you'll be taken to the permission matrix to tick what they may do."}</DialogDescription></DialogHeader>
          <div className="grid grid-cols-2 gap-1 border border-border p-1 text-xs" data-testid="user-kind-toggle">
            <button type="button" onClick={() => setForm({ ...form, kind: "human" })} className={`flex items-center justify-center gap-1.5 py-1.5 ${!isBot ? "bg-primary text-primary-foreground" : "hover:bg-accent"}`} data-testid="user-kind-human"><UserPlus className="h-3.5 w-3.5" /> Person</button>
            <button type="button" onClick={() => setForm({ ...form, kind: "bot" })} className={`flex items-center justify-center gap-1.5 py-1.5 ${isBot ? "bg-primary text-primary-foreground" : "hover:bg-accent"}`} data-testid="user-kind-bot"><Bot className="h-3.5 w-3.5" /> Bot (API key)</button>
          </div>
          <Field label={isBot ? "Bot name" : "Full name"}><Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder={isBot ? "e.g. Grok – claims triage" : ""} data-testid="user-name-input" /></Field>
          {!isBot && <Field label="Work email"><Input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mono text-xs" data-testid="user-email-input" /></Field>}
          <DialogFooter><Button type="submit" disabled={busy} data-testid="user-save-btn">{busy ? "Adding…" : isBot ? "Add bot" : "Add user"}</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function AccessSummary({ u, total }) {
  if (!u.active) return <Tag tone="red">deactivated</Tag>;
  if (u.permissions.length === total) return <Tag tone="blue">full access</Tag>;
  if (u.view_only) return <Tag tone="neutral">view only · {u.permissions.length}/{total}</Tag>;
  return <Tag tone="emerald">{u.permissions.length}/{total} capabilities</Tag>;
}

export default function Users() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { user: me, can, readOnly, startImpersonation } = useAuth();
  const [adding, setAdding] = useState(false);
  const [keysFor, setKeysFor] = useState(null);
  const { data: users = [] } = useQuery({ queryKey: ["users"], queryFn: () => api.get("/users").then((r) => r.data) });
  const { data: catalog } = useQuery({ queryKey: ["permissions"], queryFn: () => api.get("/permissions").then((r) => r.data) });
  const total = catalog?.all.length || 0;
  const patch = async (id, changes, msg) => {
    try {
      await api.patch(`/users/${id}`, changes);
      toast.success(msg);
      qc.invalidateQueries({ queryKey: ["users"] });
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div data-testid="users-page">
      <PageHeader eyebrow="People" title="Users" description="Everyone at a glance: last activity, access level, activate or deactivate, and view the app as they see it. Access itself is set in the permission matrix."
        actions={!readOnly && <Button onClick={() => setAdding(true)} data-testid="add-user-btn"><UserPlus className="mr-1.5 h-4 w-4" /> Add user</Button>} />
      <div className="p-4 md:p-6">
        <div className="surface overflow-x-auto">
          <table className="w-full text-sm" data-testid="users-table">
            <thead><tr className="border-b border-border text-left">{["User", "Access", "Last activity", "Active", ""].map((h) => <th key={h} className="eyebrow px-4 py-2 font-semibold">{h}</th>)}</tr></thead>
            <tbody className="divide-y divide-border">
              {users.map((u) => (
                <tr key={u.id} className={`row-hover ${!u.active ? "opacity-50" : ""}`} data-testid={`user-row-${u.id}`}>
                  <td className="px-4 py-2.5">
                    <div className="flex items-center gap-3">
                      <UserAvatar name={u.name} src={u.avatar_url} />
                      <div>
                        <div className="flex items-center gap-1.5 font-semibold">{u.kind === "bot" && <Bot className="h-3.5 w-3.5 text-primary" data-testid={`user-bot-icon-${u.id}`} />}{u.name} {u.id === me.id && <span className="eyebrow ml-1">you</span>}</div>
                        <div className="mono text-[11px] text-muted-foreground">{u.kind === "bot" ? `bot · ${u.api_key_count || 0} active key${u.api_key_count === 1 ? "" : "s"}` : u.email}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-2.5">
                    <div className="flex items-center gap-2" data-testid={`user-access-${u.id}`}>
                      <AccessSummary u={u} total={total} />
                      {can("permissions.manage") && <Link to="/permissions" className="inline-flex items-center gap-1 text-[11px] text-primary hover:underline" data-testid={`user-edit-access-${u.id}`}><Grid3X3 className="h-3 w-3" /> edit</Link>}
                    </div>
                  </td>
                  <td className="mono px-4 py-2.5 text-xs text-muted-foreground" data-testid={`user-last-activity-${u.id}`}>{timeAgo(u.last_activity_at)}</td>
                  <td className="px-4 py-2.5"><Switch checked={u.active} disabled={readOnly || u.id === me.id} onCheckedChange={(v) => patch(u.id, { active: v }, v ? `${u.name} reactivated` : `${u.name} deactivated and signed out`)} data-testid={`user-active-${u.id}`} /></td>
                  <td className="px-4 py-2.5 text-right">
                    {u.kind === "bot" && can("permissions.manage") && !readOnly && (
                      <Button variant="outline" size="sm" className="mr-1 h-7 text-xs" onClick={() => setKeysFor(u)} data-testid={`bot-keys-${u.id}`}><KeyRound className="mr-1 h-3 w-3" /> API keys</Button>
                    )}
                    {can("users.impersonate") && !readOnly && u.active && u.id !== me.id && (
                      <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => startImpersonation(u.id).then((t) => { toast.info(`Viewing as ${t.name}`); navigate("/tickets"); })} data-testid={`view-as-${u.id}`}><Eye className="mr-1 h-3 w-3" /> View as</Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <UserDialog open={adding} onOpenChange={setAdding} canMatrix={can("permissions.manage")} onBotCreated={(b) => setKeysFor(b)} />
      <BotKeysDialog bot={keysFor} onOpenChange={(v) => { if (!v) setKeysFor(null); }} />
    </div>
  );
}
